import React from "react"
import { Box } from "@mui/material"
import { PANEL_TEXT_MUTED } from "@/theme/tokens"

const finite = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null)

// Wave 1 decimal; wind/current up to 2 decimals without trailing zeros.
export const formatWeatherValue = (value, param) =>
  param === "wave" ? value.toFixed(1) : String(Number(value.toFixed(2)))

// Degrees by convention (wind/wave: coming from, current: setting to); the arrow always shows the flow.
export const flowDeg = (param, dir) => (param === "current" ? dir : dir + 180)

export function DirectionArrow({ deg, size = 10, color = PANEL_TEXT_MUTED }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{ width: size, height: size, flex: "none", transform: `rotate(${deg}deg)` }}
    >
      <path d="M12 3l6 17-6-4-6 4z" fill={color} />
    </Box>
  )
}

// Speed and direction from U/V (direction the flow comes from), else null.
function fromUV(u, v) {
  if (!Number.isFinite(u) || !Number.isFinite(v)) return null
  const deg = (270 - (Math.atan2(v, u) * 180) / Math.PI + 360) % 360
  return { speed: Math.sqrt(u * u + v * v), dir: deg }
}

// Current is given as "setting towards" (astar convention), unlike wind and wave.
const toward = (flow) => flow && { ...flow, dir: (flow.dir + 180) % 360 }

// { wave, wind, current } as { speed, dir } of a waypoint: snapshot first, U/V fallback.
export function waypointWeather(props) {
  const ws = props?.weather_snapshot ?? {}
  const wind =
    finite(ws.wind_gfs_speed_knots_interp ?? ws.wind_speed_knots) != null
      ? {
          speed: finite(ws.wind_gfs_speed_knots_interp ?? ws.wind_speed_knots),
          dir: finite(ws.wind_gfs_direction_deg_interp ?? ws.wind_direction_deg),
        }
      : fromUV(props?.u10, props?.v10)
  const current =
    finite(ws.current_hycom_speed_knots_interp ?? ws.current_speed_knots) != null
      ? {
          speed: finite(ws.current_hycom_speed_knots_interp ?? ws.current_speed_knots),
          dir: finite(ws.current_hycom_direction_deg_interp ?? ws.current_direction_deg),
        }
      : toward(fromUV(props?.ucur, props?.vcur))
  return {
    wave: {
      speed: finite(ws.wave_nww3_significant_height_m_interp ?? ws.wave_swh ?? props?.hssw),
      dir: finite(ws.wave_nww3_mean_direction_deg_interp ?? ws.wave_dirpw ?? props?.dmean),
    },
    wind,
    current,
  }
}

// Exceeded only when the shown (rounded) value is above the limit, as in limitTone.
export const aboveLimit = (value, limit, param) =>
  value != null && limit > 0 && Number(formatWeatherValue(value, param)) > limit
